/**
 * ATS (Applicant Tracking System) Resume Keywords
 *
 * Helps users optimize their resumes with:
 * - Hard skills (technical, software, tools)
 * - Soft skills (leadership, communication)
 * - Industry-specific terminology
 * - Action verbs for achievements
 * - Resume section templates
 */

import { CareerPath } from './careerPaths';

export interface ATSKeywordSet {
  careerId: string;
  hardSkills: Keyword[];
  softSkills: Keyword[];
  industryTerms: Keyword[];
  actionVerbs: string[];
  certifications: Keyword[];
  resumeTips: ResumeTip[];
  sampleBullets: SampleBullet[];
}

export interface Keyword {
  term: string;
  importance: 'critical' | 'high' | 'medium';
  context?: string; // When/how to use this keyword
}

export interface ResumeTip {
  section: string;
  tip: string;
}

export interface SampleBullet {
  weak: string;
  strong: string;
  explanation: string;
}

// Career-specific ATS keyword sets
const ATS_KEYWORD_SETS: Record<string, ATSKeywordSet> = {
  web_developer: {
    careerId: 'web_developer',
    hardSkills: [
      { term: 'JavaScript', importance: 'critical', context: 'Include in skills section and describe in experience' },
      { term: 'HTML5', importance: 'critical' },
      { term: 'CSS3', importance: 'critical' },
      { term: 'React', importance: 'critical', context: 'Or Vue/Angular - match the job posting' },
      { term: 'TypeScript', importance: 'high' },
      { term: 'Node.js', importance: 'high' },
      { term: 'Git', importance: 'critical' },
      { term: 'REST APIs', importance: 'high' },
      { term: 'SQL', importance: 'medium' },
      { term: 'MongoDB', importance: 'medium' },
      { term: 'Webpack', importance: 'medium' },
      { term: 'Jest', importance: 'medium', context: 'Or other testing frameworks' },
      { term: 'Responsive Design', importance: 'critical' },
      { term: 'CSS Flexbox', importance: 'high' },
      { term: 'CSS Grid', importance: 'high' },
      { term: 'SCSS/Sass', importance: 'medium' },
      { term: 'Next.js', importance: 'high' },
      { term: 'GraphQL', importance: 'medium' },
      { term: 'Docker', importance: 'medium' },
      { term: 'AWS', importance: 'medium' },
    ],
    softSkills: [
      { term: 'Problem-solving', importance: 'critical' },
      { term: 'Collaboration', importance: 'high' },
      { term: 'Communication', importance: 'high' },
      { term: 'Attention to detail', importance: 'high' },
      { term: 'Self-motivated', importance: 'medium' },
      { term: 'Time management', importance: 'medium' },
    ],
    industryTerms: [
      { term: 'Agile', importance: 'high' },
      { term: 'Scrum', importance: 'high' },
      { term: 'CI/CD', importance: 'medium' },
      { term: 'Code review', importance: 'high' },
      { term: 'Version control', importance: 'critical' },
      { term: 'Unit testing', importance: 'high' },
      { term: 'User experience', importance: 'high' },
      { term: 'Performance optimization', importance: 'medium' },
      { term: 'Cross-browser compatibility', importance: 'high' },
      { term: 'Mobile-first', importance: 'high' },
    ],
    actionVerbs: [
      'Developed', 'Built', 'Implemented', 'Designed', 'Created',
      'Optimized', 'Integrated', 'Deployed', 'Maintained', 'Debugged',
      'Refactored', 'Automated', 'Collaborated', 'Led', 'Mentored',
    ],
    certifications: [
      { term: 'AWS Certified Developer', importance: 'medium' },
      { term: 'Google Mobile Web Specialist', importance: 'medium' },
      { term: 'Meta Front-End Developer', importance: 'medium' },
    ],
    resumeTips: [
      { section: 'Projects', tip: 'Include 2-3 portfolio projects with live links and GitHub repos' },
      { section: 'Skills', tip: 'List technologies exactly as they appear in job postings (React not ReactJS)' },
      { section: 'Experience', tip: 'Quantify impact: "Reduced page load time by 40%" not "Improved performance"' },
    ],
    sampleBullets: [
      {
        weak: 'Worked on website features',
        strong: 'Developed 15+ responsive React components, reducing development time for new features by 30%',
        explanation: 'Includes technology, quantity, and measurable impact',
      },
      {
        weak: 'Fixed bugs',
        strong: 'Resolved 50+ production bugs across JavaScript, CSS, and API integrations, improving site stability and user satisfaction',
        explanation: 'Quantifies work and explains the benefit',
      },
    ],
  },

  customer_success_specialist: {
    careerId: 'customer_success_specialist',
    hardSkills: [
      { term: 'Salesforce', importance: 'critical', context: 'Or your specific CRM experience' },
      { term: 'HubSpot', importance: 'high' },
      { term: 'CRM', importance: 'critical' },
      { term: 'Gainsight', importance: 'high' },
      { term: 'Zendesk', importance: 'medium' },
      { term: 'Intercom', importance: 'medium' },
      { term: 'Excel', importance: 'high' },
      { term: 'Data analysis', importance: 'high' },
      { term: 'Slack', importance: 'medium' },
      { term: 'Zoom', importance: 'medium' },
    ],
    softSkills: [
      { term: 'Relationship building', importance: 'critical' },
      { term: 'Communication', importance: 'critical' },
      { term: 'Empathy', importance: 'high' },
      { term: 'Problem-solving', importance: 'critical' },
      { term: 'Active listening', importance: 'high' },
      { term: 'Negotiation', importance: 'medium' },
      { term: 'Time management', importance: 'high' },
      { term: 'Adaptability', importance: 'medium' },
    ],
    industryTerms: [
      { term: 'Customer retention', importance: 'critical' },
      { term: 'Churn prevention', importance: 'critical' },
      { term: 'Onboarding', importance: 'critical' },
      { term: 'NPS', importance: 'high', context: 'Net Promoter Score' },
      { term: 'QBR', importance: 'high', context: 'Quarterly Business Review' },
      { term: 'Upselling', importance: 'high' },
      { term: 'Cross-selling', importance: 'medium' },
      { term: 'SaaS', importance: 'critical' },
      { term: 'ARR', importance: 'high', context: 'Annual Recurring Revenue' },
      { term: 'Customer health score', importance: 'high' },
      { term: 'Customer lifecycle', importance: 'high' },
      { term: 'Product adoption', importance: 'high' },
    ],
    actionVerbs: [
      'Managed', 'Retained', 'Onboarded', 'Resolved', 'Expanded',
      'Grew', 'Improved', 'Advocated', 'Collaborated', 'Analyzed',
      'Presented', 'Trained', 'Monitored', 'Achieved', 'Exceeded',
    ],
    certifications: [
      { term: 'Salesforce Administrator', importance: 'high' },
      { term: 'HubSpot Inbound', importance: 'medium' },
      { term: 'Gainsight Certified', importance: 'medium' },
      { term: 'Success Coaching', importance: 'medium' },
    ],
    resumeTips: [
      { section: 'Metrics', tip: 'Include retention rates, NPS improvements, and revenue expansion numbers' },
      { section: 'Experience', tip: 'Mention portfolio size: "Managed 50+ enterprise accounts"' },
      { section: 'Skills', tip: 'List specific CRM platforms and CS tools you\'ve used' },
    ],
    sampleBullets: [
      {
        weak: 'Helped customers with their issues',
        strong: 'Managed portfolio of 45 SMB accounts ($1.2M ARR), achieving 95% retention rate and 20% expansion revenue',
        explanation: 'Quantifies portfolio, revenue, and key metrics',
      },
      {
        weak: 'Did customer onboarding',
        strong: 'Designed and executed onboarding program for 100+ new customers, reducing time-to-value by 35%',
        explanation: 'Shows ownership and measurable improvement',
      },
    ],
  },

  project_manager: {
    careerId: 'project_manager',
    hardSkills: [
      { term: 'Microsoft Project', importance: 'high' },
      { term: 'Jira', importance: 'critical' },
      { term: 'Asana', importance: 'high' },
      { term: 'Monday.com', importance: 'medium' },
      { term: 'Trello', importance: 'medium' },
      { term: 'Confluence', importance: 'high' },
      { term: 'Excel', importance: 'critical' },
      { term: 'PowerPoint', importance: 'high' },
      { term: 'Gantt charts', importance: 'high' },
      { term: 'Smartsheet', importance: 'medium' },
    ],
    softSkills: [
      { term: 'Leadership', importance: 'critical' },
      { term: 'Communication', importance: 'critical' },
      { term: 'Stakeholder management', importance: 'critical' },
      { term: 'Problem-solving', importance: 'high' },
      { term: 'Negotiation', importance: 'high' },
      { term: 'Conflict resolution', importance: 'medium' },
      { term: 'Decision-making', importance: 'high' },
    ],
    industryTerms: [
      { term: 'Agile', importance: 'critical' },
      { term: 'Scrum', importance: 'critical' },
      { term: 'Kanban', importance: 'high' },
      { term: 'Waterfall', importance: 'high' },
      { term: 'Sprint planning', importance: 'high' },
      { term: 'Risk management', importance: 'critical' },
      { term: 'Budget management', importance: 'critical' },
      { term: 'Resource allocation', importance: 'high' },
      { term: 'Scope management', importance: 'high' },
      { term: 'Change management', importance: 'high' },
      { term: 'Milestone tracking', importance: 'high' },
      { term: 'KPIs', importance: 'high' },
    ],
    actionVerbs: [
      'Led', 'Managed', 'Delivered', 'Coordinated', 'Executed',
      'Planned', 'Directed', 'Facilitated', 'Implemented', 'Streamlined',
      'Optimized', 'Tracked', 'Reported', 'Mitigated', 'Allocated',
    ],
    certifications: [
      { term: 'PMP', importance: 'critical', context: 'Project Management Professional' },
      { term: 'Certified Scrum Master (CSM)', importance: 'high' },
      { term: 'CAPM', importance: 'high', context: 'Certified Associate in Project Management' },
      { term: 'PMI-ACP', importance: 'medium' },
      { term: 'PRINCE2', importance: 'medium' },
      { term: 'Six Sigma', importance: 'medium' },
    ],
    resumeTips: [
      { section: 'Summary', tip: 'Lead with certifications and years of experience' },
      { section: 'Experience', tip: 'Include project budget sizes, team sizes, and on-time delivery rates' },
      { section: 'Projects', tip: 'List 2-3 significant projects with outcomes and scope' },
    ],
    sampleBullets: [
      {
        weak: 'Managed projects at the company',
        strong: 'Led 12 cross-functional projects ($500K-$2M budgets) with 100% on-time delivery rate using Agile methodology',
        explanation: 'Quantifies number, budget, success rate, and methodology',
      },
      {
        weak: 'Worked with team members',
        strong: 'Managed team of 8 developers and designers, improving sprint velocity by 25% through process optimization',
        explanation: 'Shows team size, metric improvement, and how it was achieved',
      },
    ],
  },

  electrician: {
    careerId: 'electrician',
    hardSkills: [
      { term: 'Electrical installation', importance: 'critical' },
      { term: 'Wiring', importance: 'critical' },
      { term: 'Conduit bending', importance: 'high' },
      { term: 'Blueprint reading', importance: 'critical' },
      { term: 'Troubleshooting', importance: 'critical' },
      { term: 'Multimeter', importance: 'high' },
      { term: 'Panel installation', importance: 'high' },
      { term: 'Motor controls', importance: 'medium' },
      { term: 'PLC', importance: 'medium' },
      { term: 'Fire alarm systems', importance: 'medium' },
    ],
    softSkills: [
      { term: 'Safety consciousness', importance: 'critical' },
      { term: 'Attention to detail', importance: 'critical' },
      { term: 'Problem-solving', importance: 'high' },
      { term: 'Physical stamina', importance: 'high' },
      { term: 'Communication', importance: 'medium' },
      { term: 'Teamwork', importance: 'high' },
    ],
    industryTerms: [
      { term: 'NEC', importance: 'critical', context: 'National Electrical Code' },
      { term: 'OSHA', importance: 'critical' },
      { term: 'Lockout/tagout', importance: 'high' },
      { term: 'Commercial electrical', importance: 'high' },
      { term: 'Residential electrical', importance: 'high' },
      { term: 'Industrial electrical', importance: 'medium' },
      { term: 'Arc flash', importance: 'medium' },
      { term: 'Grounding', importance: 'high' },
      { term: 'Load calculations', importance: 'high' },
    ],
    actionVerbs: [
      'Installed', 'Maintained', 'Repaired', 'Troubleshot', 'Wired',
      'Tested', 'Inspected', 'Upgraded', 'Completed', 'Ensured',
      'Followed', 'Trained', 'Supervised', 'Complied', 'Documented',
    ],
    certifications: [
      { term: 'Journeyman Electrician License', importance: 'critical' },
      { term: 'OSHA 10', importance: 'critical' },
      { term: 'OSHA 30', importance: 'high' },
      { term: 'Master Electrician License', importance: 'high' },
      { term: 'EPA 608', importance: 'medium' },
    ],
    resumeTips: [
      { section: 'Licenses', tip: 'List state license numbers and expiration dates' },
      { section: 'Experience', tip: 'Include types of projects (commercial, residential) and scale' },
      { section: 'Safety', tip: 'Mention safety record and any safety training completed' },
    ],
    sampleBullets: [
      {
        weak: 'Did electrical work',
        strong: 'Installed electrical systems for 25+ residential new construction projects, ensuring 100% NEC compliance',
        explanation: 'Quantifies projects and emphasizes code compliance',
      },
      {
        weak: 'Fixed electrical problems',
        strong: 'Troubleshot and repaired electrical faults in commercial buildings, reducing downtime by an average of 4 hours per incident',
        explanation: 'Shows specialization and measurable benefit',
      },
    ],
  },

  medical_assistant: {
    careerId: 'medical_assistant',
    hardSkills: [
      { term: 'Vital signs', importance: 'critical' },
      { term: 'Phlebotomy', importance: 'critical' },
      { term: 'EHR', importance: 'critical', context: 'Electronic Health Records' },
      { term: 'Epic', importance: 'high', context: 'Or your specific EHR system' },
      { term: 'Medical terminology', importance: 'critical' },
      { term: 'EKG', importance: 'high' },
      { term: 'Injections', importance: 'high' },
      { term: 'Patient intake', importance: 'critical' },
      { term: 'Specimen collection', importance: 'high' },
      { term: 'Wound care', importance: 'medium' },
    ],
    softSkills: [
      { term: 'Compassion', importance: 'critical' },
      { term: 'Communication', importance: 'critical' },
      { term: 'Attention to detail', importance: 'critical' },
      { term: 'Organization', importance: 'high' },
      { term: 'Multitasking', importance: 'high' },
      { term: 'Empathy', importance: 'high' },
      { term: 'Professionalism', importance: 'high' },
    ],
    industryTerms: [
      { term: 'HIPAA', importance: 'critical' },
      { term: 'Clinical procedures', importance: 'critical' },
      { term: 'Patient care', importance: 'critical' },
      { term: 'Medical charting', importance: 'high' },
      { term: 'Infection control', importance: 'high' },
      { term: 'Patient education', importance: 'high' },
      { term: 'Appointment scheduling', importance: 'medium' },
      { term: 'Insurance verification', importance: 'medium' },
    ],
    actionVerbs: [
      'Assisted', 'Administered', 'Documented', 'Prepared', 'Performed',
      'Collected', 'Monitored', 'Educated', 'Scheduled', 'Maintained',
      'Communicated', 'Ensured', 'Supported', 'Coordinated', 'Verified',
    ],
    certifications: [
      { term: 'CMA', importance: 'critical', context: 'Certified Medical Assistant' },
      { term: 'RMA', importance: 'critical', context: 'Registered Medical Assistant' },
      { term: 'CPR/BLS', importance: 'critical' },
      { term: 'Phlebotomy Certification', importance: 'high' },
      { term: 'CCMA', importance: 'high' },
    ],
    resumeTips: [
      { section: 'Certifications', tip: 'List CMA/RMA certification prominently with certification number' },
      { section: 'Clinical Skills', tip: 'Create a skills section specifically for clinical competencies' },
      { section: 'Experience', tip: 'Include patient volume and specialty areas' },
    ],
    sampleBullets: [
      {
        weak: 'Helped doctors with patients',
        strong: 'Performed patient intake for 30+ patients daily, including vital signs, medical history, and EHR documentation',
        explanation: 'Quantifies volume and lists specific tasks',
      },
      {
        weak: 'Drew blood samples',
        strong: 'Collected blood specimens from 15-20 patients daily with 98% first-stick success rate, maintaining OSHA and HIPAA compliance',
        explanation: 'Includes quantity, quality metric, and compliance',
      },
    ],
  },
};

/**
 * Get ATS keyword set for a specific career
 */
export function getATSKeywords(career: CareerPath): ATSKeywordSet {
  if (ATS_KEYWORD_SETS[career.id]) {
    return ATS_KEYWORD_SETS[career.id];
  }

  // Generate generic keyword set
  return generateGenericATSKeywords(career);
}

/**
 * Generate generic ATS keywords for careers without specific data
 */
function generateGenericATSKeywords(career: CareerPath): ATSKeywordSet {
  return {
    careerId: career.id,
    hardSkills: career.requiredSkills.map((skill, idx) => ({
      term: skill,
      importance: idx < 3 ? 'critical' as const : idx < 6 ? 'high' as const : 'medium' as const,
    })),
    softSkills: [
      { term: 'Communication', importance: 'critical' },
      { term: 'Problem-solving', importance: 'critical' },
      { term: 'Teamwork', importance: 'high' },
      { term: 'Adaptability', importance: 'high' },
      { term: 'Time management', importance: 'medium' },
      { term: 'Attention to detail', importance: 'high' },
    ],
    industryTerms: career.keywords.slice(0, 8).map((kw, idx) => ({
      term: kw,
      importance: idx < 2 ? 'critical' as const : idx < 5 ? 'high' as const : 'medium' as const,
    })),
    actionVerbs: [
      'Achieved', 'Improved', 'Managed', 'Created', 'Developed',
      'Led', 'Implemented', 'Designed', 'Delivered', 'Collaborated',
    ],
    certifications: [],
    resumeTips: [
      { section: 'Skills', tip: 'Match keywords exactly as they appear in job postings' },
      { section: 'Experience', tip: 'Use numbers and metrics to quantify your achievements' },
      { section: 'Format', tip: 'Use a clean, ATS-friendly format without tables or graphics' },
    ],
    sampleBullets: [
      {
        weak: 'Responsible for various tasks',
        strong: `Accomplished [specific achievement] by [action taken], resulting in [measurable outcome]`,
        explanation: 'Always include action, what you did, and the result',
      },
    ],
  };
}

/**
 * Calculate keyword match score between a resume and job description
 */
export function calculateKeywordMatch(
  resumeText: string,
  keywords: ATSKeywordSet
): { score: number; matches: string[]; missing: string[] } {
  const resumeLower = resumeText.toLowerCase();
  const matches: string[] = [];
  const missing: string[] = [];

  // Check critical keywords
  const criticalKeywords = [
    ...keywords.hardSkills.filter(k => k.importance === 'critical'),
    ...keywords.softSkills.filter(k => k.importance === 'critical'),
    ...keywords.industryTerms.filter(k => k.importance === 'critical'),
  ];

  for (const keyword of criticalKeywords) {
    if (resumeLower.includes(keyword.term.toLowerCase())) {
      matches.push(keyword.term);
    } else {
      missing.push(keyword.term);
    }
  }

  const score = criticalKeywords.length > 0
    ? Math.round((matches.length / criticalKeywords.length) * 100)
    : 0;

  return { score, matches, missing };
}
